// src/theme.js

// ─── Environment TTLs ─────────────────────────────────────────────────────────
// HOLD_TTL_MS:        how long a slot stays "held" while a user is selecting
// RESERVATION_TTL_MS: how long a submitted (unapproved) reservation persists
export const TTL = {
  HOLD_MS:        5 * 60 * 1000,        // 5 min
  RESERVATION_MS: 2 * 60 * 60 * 1000,   // 2 hours
};

// ─── Brand colours ────────────────────────────────────────────────────────────
export const COLOR = {
  primary:       "red-700",
  primaryHover:  "red-600",
  primaryLight:  "red-600",
  primaryDim:    "red-900",
  surface:       "black",
  surfaceCard:   "zinc-950",
  surfaceSubtle: "white/[0.02]",
  surfaceHover:  "white/5",
  textPrimary:   "white",
  textMuted:     "white/50",
  textDim:       "white/30",
  textHint:      "white/20",
  borderDefault: "white/10",
  borderHover:   "white/25",
  borderStrong:  "white/15",
};

// ─── Reusable Tailwind class strings ─────────────────────────────────────────
export const btn = {
  primary:
    "px-5 py-2.5 bg-red-700 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold uppercase tracking-widest text-[11px] transition-colors duration-200",
  primaryFull:
    "w-full py-3 bg-red-700 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold uppercase tracking-widest text-xs transition-colors duration-200",
  ghost:
    "px-4 py-2 border border-white/10 hover:border-white/25 text-white/40 hover:text-white/70 font-semibold uppercase tracking-widest text-[10px] transition-colors duration-200",
  danger:
    "px-4 py-2 border border-red-900/60 hover:border-red-600 text-red-500/60 hover:text-red-400 font-bold uppercase tracking-widest text-[10px] transition-colors duration-200",
  toggle: (active) =>
    `px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors duration-200 ${
      active
        ? "bg-red-700 text-white"
        : "border border-white/10 text-white/40 hover:text-white/70"
    }`,
};

export const input = {
  base:
    "w-full bg-white/5 border border-white/10 text-white placeholder-white/25 px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors duration-200",
  dark:
    "w-full bg-black/40 border border-white/15 text-white px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors duration-200",
  label:
    "block text-[10px] uppercase tracking-[0.25em] text-white/40 font-semibold mb-2",
  error:
    "mt-1.5 text-[11px] text-red-500 tracking-wide",
};

export const card = {
  base:   "bg-white/[0.03] border border-white/10 backdrop-blur-sm",
  subtle: "border border-white/10 bg-white/[0.02]",
};

export const hero = {
  wrapper:  "relative w-full pt-24 pb-12 px-6 md:px-12 overflow-hidden",
  gradient: "radial-gradient(ellipse at 70% 50%, rgba(185,28,28,0.07) 0%, transparent 70%)",
  divider:  "absolute bottom-0 left-0 w-full h-px bg-white/5",
  inner:    "max-w-5xl mx-auto relative",
  accent:   "text-[10px] uppercase tracking-[0.3em] text-red-600 font-semibold mb-4",
  bar:      "w-10 h-0.5 bg-red-700 mb-5",
  subtitle: "text-sm text-white/40 max-w-sm leading-relaxed",
};

// ─── Slot status colours ──────────────────────────────────────────────────────
// Keys match exactly what useSlots returns: "confirmed" | "reserved" |
// "held-other" | "held-me" | "available"
export const slotStatus = {
  confirmed:    "bg-red-900/40 border-red-900 text-red-500/60 line-through cursor-not-allowed opacity-60",
  reserved:     "bg-red-900/40 border-red-900 text-red-500/60 line-through cursor-not-allowed opacity-60",
  "held-other": "bg-amber-900/30 border-amber-700/50 text-amber-400/70 cursor-not-allowed",
  "held-me":    "bg-red-700 border-red-500 text-white font-bold shadow-lg shadow-red-900/40 scale-[1.02]",
  available:    "bg-white/5 border-white/10 text-white/70 hover:border-red-600/60 hover:bg-red-950/30 cursor-pointer",
};